import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CustomerManagementService } from 'src/app/shared/services/customer.service';
import { CustomerAddress } from 'src/app/shared/models/invoice/invoice.model';
import { SettingsInvoice } from 'src/app/invoice/models/invoice.model';
import { Customer } from 'src/app/customer/models/customer.model';

@Component({
  selector: 'app-card-pay',
  templateUrl: './card-pay.component.html',
  styleUrls: ['./card-pay.component.scss'],
})
export class CardPayComponent implements OnInit, OnDestroy {
  @ViewChild('cardPayForm', { static: false }) cardForm: HTMLFormElement;

  @Input() invoice: any;
  @Input() invoiceSettings: SettingsInvoice;
  @Input() businessInfo: any;
  @Input() customer: Customer;
  @Input() customerAddresses: any;
  @Input() invoiceBalance: any = 0;
  @Input() addressId: number;
  @Input() addressShippingId: number;
  @Input() isProcessing: boolean = false;
  @Input() isSubmitted: boolean = false;
  @Input() cardCVV: string;
  @Input() selectedCard: any;
  @Input() publicAPIKey: any;

  @Output() onValueChange: EventEmitter<any> = new EventEmitter();
  @Output() onAddressAdd: EventEmitter<boolean> = new EventEmitter();
  @Output() onCardAdd: EventEmitter<boolean> = new EventEmitter();
  @Output() onCardSelect: EventEmitter<any> = new EventEmitter();
  @Output() onTokenReceive: EventEmitter<any> = new EventEmitter();

  isInitializing: boolean = false;
  isAddCard: boolean = false;
  customerAddress: CustomerAddress = new CustomerAddress();
  customerCards: any[] = [];

  readonly unsubscribe$ = new Subject();

  constructor(private customerService: CustomerManagementService) {}

  ngOnInit(): void {
    this.isInitializing = true;

    this.getDefaultCustomerAddress();

    if (this.customer && [2, 3].includes(this.customer.LevelProcessing)) {
      this.getShippingCustomerAddress();
    }

    this.customerService
      .getCustomerCardsOnFile(this.invoice.customerId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        this.customerCards = data;
        this.getDefaultCard();

        this.isInitializing = false;

        this.onValueChange.emit(this.cardForm.form);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getDefaultCard(): void {
    if (this.customerCards.length && !this.selectedCard) {
      const card = this.customerCards.find((e) => e.IsDefault);
      this.onSelectCard(card?.Id || this.customerCards[0].Id);
    }
  }

  getDefaultCustomerAddress(): void {
    if (this.customerAddresses) {
      const address = this.customerAddresses.find((e) => {
        if (this.addressId) {
          return e.Id === this.addressId;
        }

        return e.IsDefault;
      });

      if (address) {
        this.addressId = address.Id;
      }
    }
  }

  getShippingCustomerAddress(): void {
    if (this.customerAddresses) {
      const address = this.customerAddresses.find((e) => {
        if (this.addressShippingId) {
          return e.Id === this.addressShippingId;
        }

        return e.AddressType === 3 || e.IsDefault;
      });

      if (address) {
        this.addressShippingId = address.Id;
      }
    }
  }

  getFullAddress(address: any): string {
    return `${address.StreetAddress || ''} ${address.City || ''} ${
      address.CountryCode || ''
    } ${address.ZipCode || ''}`;
  }

  setCVVLength(): number {
    if (this.customerCards.length) {
      const selectedCard = this.customerCards.find(
        (card) => this.selectedCard?.Id === card.Id
      );

      if (selectedCard?.CardBrand.toLowerCase() === 'amex') {
        return 4;
      }

      return 3;
    }
  }

  onAddressSelected(id: number, isShipping: boolean = false): void {
    const address = this.customerAddresses.find((e) => {
      return e.Id === id;
    });

    if (address) {
      this[!isShipping ? 'addressId' : 'addressShippingId'] = address.Id;
      this.onValueChange.emit(this.cardForm.form);
    }
  }

  onChange(): void {
    this.onValueChange.emit(this.cardForm.form);
  }

  onCVVEntry(event: KeyboardEvent): void {
    if (
      !/^\d+$/.test(event.key) &&
      ![37, 38, 39, 40, 8, 46].includes(event.keyCode)
    ) {
      event.preventDefault();
    }
  }

  onSelectCard(id: number): void {
    const card = this.customerCards.find((e) => e.Id === id);

    if (card) {
      this.selectedCard = card;
      this.onCardSelect.emit(this.selectedCard);
    }
  }

  onAddCard(): void {
    this.isAddCard = !this.isAddCard;
    this.onCardAdd.emit(this.isAddCard);
  }

  onCardTokenReceived(token: any): void {
    this.onTokenReceive.emit({
      token: token,
      addressId: this.addressId,
      addressShippingId: this.addressShippingId,
    });
  }
}
